// ================================================================
// GAME STATS HOOK - Project Phoenix Refactored
// Mirrors backend "calculate on-demand" approach for guest users
// ================================================================

import { useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getCentralDateString } from '../utils/centralTime';
import { getAnonymousId } from '../utils/anonymousId';
import apiService from '../services/api';

const useGameStats = (gameId) => {
  const { user, isAuthenticated } = useAuth();

  // ================================================================
  // PROJECT PHOENIX: REFACTORED LOCAL STORAGE STREAK LOGIC FOR GUESTS
  // Now mirrors backend's "calculate on-demand" philosophy
  // ================================================================

  /**
   * Calculate current streak for a guest from a history of win dates.
   * This mirrors the backend calculateTrueCurrentStreak logic.
   *
   * @param {string[]} winHistory - An array of 'YYYY-MM-DD' date strings
   * @param {Date} currentDate - Optional current date (defaults to now)
   * @returns {number} The calculated current streak
   */
  const calculateLocalStreak = useCallback((winHistory, currentDate = new Date()) => {
    if (!winHistory || winHistory.length === 0) return 0;

    const sortedDates = winHistory.map(d => new Date(d)).sort((a, b) => b - a);
    const today = new Date(getCentralDateString());

    // Check if streak is broken (same logic as backend)
    const daysSinceLastWin = Math.round((today - sortedDates[0]) / (1000 * 60 * 60 * 24));
    if (daysSinceLastWin > 1) return 0;

    // Calculate consecutive days working backwards from most recent win
    let streak = 0;
    let expectedDate = sortedDates[0];

    for (const date of sortedDates) {
      const diff = Math.round((expectedDate - date) / (1000 * 60 * 60 * 24));
      if (diff === 0) {
        streak++;
        expectedDate.setDate(expectedDate.getDate() - 1);
      } else {
        break; // Gap in streak
      }
    }

    return streak;
  }, []);

  /**
   * Record today's daily win for a guest user in localStorage.
   * PROJECT PHOENIX: Now stores array of win dates instead of count + lastDate
   *
   * @param {string} winHistoryKey - The localStorage key for this game's win history
   * @returns {number} The new current streak
   */
  const recordLocalDailyWin = useCallback((winHistoryKey) => {
    try {
      const todayStr = getCentralDateString();
      const savedHistory = localStorage.getItem(winHistoryKey);
      let history = [];

      // Handle migration from old format to new format
      if (savedHistory) {
        const parsed = JSON.parse(savedHistory);

        if (Array.isArray(parsed)) {
          // New format: array of date strings
          history = parsed;
        } else if (parsed.count && parsed.lastCompletedDate) {
          // Old format: { count, lastCompletedDate } - migrate to new format
          console.log('Migrating guest streak from old format to new format');
          history = [];

          // Reconstruct history from old format (best effort)
          const lastDate = new Date(parsed.lastCompletedDate);
          for (let i = 0; i < parsed.count; i++) {
            const date = new Date(lastDate);
            date.setDate(date.getDate() - i);
            history.push(date.toISOString().split('T')[0]);
          }
        }
      }

      // Add today's win if not already present
      if (!history.includes(todayStr)) {
        history.push(todayStr);
        localStorage.setItem(winHistoryKey, JSON.stringify(history));
      }

      // Calculate current streak
      const currentStreak = calculateLocalStreak(history);

      // Update best streak if current streak is higher
      const bestStreakKey = `${winHistoryKey}_best`;
      const savedBestStreak = localStorage.getItem(bestStreakKey);
      let bestStreak = savedBestStreak ? parseInt(savedBestStreak, 10) : 0;

      if (currentStreak > bestStreak) {
        bestStreak = currentStreak;
        localStorage.setItem(bestStreakKey, bestStreak.toString());
      }

      return currentStreak;
    } catch (error) {
      console.error('Error recording local daily win:', error);
      return 0;
    }
  }, [calculateLocalStreak]);

  /**
   * Track game completion for both authenticated and anonymous users
   * PROJECT PHOENIX: This now works with our new backend that only stores counters and best_daily_streak
   * ANONYMOUS TRACKING: Anonymous users get server-side tracking + localStorage fallback
   *
   * @param {boolean} isDaily - Whether this is a daily game completion
   * @param {number} attemptsCount - Optional number of attempts for immediate achievement checking (e.g., Flawless Victory)
   */
  const trackGameCompletion = useCallback(async (isDaily = false, attemptsCount = undefined) => {
    try {
      if (isAuthenticated && user) {
        // Authenticated user path - use existing endpoint
        const requestBody = { is_daily: isDaily };

        // Only include attempts_count if provided (for immediate achievement checking)
        if (attemptsCount !== undefined && typeof attemptsCount === 'number') {
          requestBody.attempts_count = attemptsCount;
        }

        const response = await apiService.post(
          `/api/game-stats/user/${user.id}/game/${gameId}`,
          requestBody
        );

        const updatedStats = await response.json();
        return updatedStats;
      } else {
        // Anonymous user path - track via anonymous endpoint
        const anonymousId = getAnonymousId();
        const requestBody = {
          anonymous_id: anonymousId,
          is_daily: isDaily
        };

        try {
          const response = await apiService.post(
            `/api/game-stats/anonymous/${gameId}`,
            requestBody
          );

          const result = await response.json();

          // Also update localStorage as fallback for anonymous users
          if (isDaily) {
            const winHistoryKey = `dailyWinHistory_${gameId}`;
            recordLocalDailyWin(winHistoryKey);
          }

          return result;
        } catch (serverError) {
          console.warn('Anonymous server tracking failed, using localStorage fallback:', serverError);

          // Fallback to localStorage-only tracking for anonymous users
          if (isDaily) {
            const winHistoryKey = `dailyWinHistory_${gameId}`;
            const currentStreak = recordLocalDailyWin(winHistoryKey);
            return {
              message: 'Game tracked locally (server unavailable)',
              currentStreak,
              gameId,
              isDaily
            };
          }

          return { message: 'Game completed (offline mode)' };
        }
      }
    } catch (error) {
      console.error('Error tracking game completion:', error);

      // Final fallback for daily games - always use localStorage
      if (isDaily && !isAuthenticated) {
        try {
          const winHistoryKey = `dailyWinHistory_${gameId}`;
          const currentStreak = recordLocalDailyWin(winHistoryKey);
          return {
            message: 'Game tracked locally (fallback)',
            currentStreak,
            gameId,
            isDaily
          };
        } catch (localError) {
          console.error('localStorage fallback also failed:', localError);
        }
      }

      return null;
    }
  }, [isAuthenticated, user, gameId, recordLocalDailyWin]);

  /**
   * Get game statistics for authenticated users
   */
  const getGameStats = useCallback(async () => {
    if (!isAuthenticated || !user) {
      return null;
    }

    try {
      const response = await apiService.get(`/api/game-stats/user/${user.id}/game/${gameId}`);
      return await response.json();
    } catch (error) {
      console.error('Error fetching game stats:', error);
      return null;
    }
  }, [isAuthenticated, user, gameId]);


  /**
   * Get the best streak for a guest from localStorage.
   * 
   * @param {string} winHistoryKey - The localStorage key for this game's win history
   * @returns {number} The best streak achieved
   */
  const getLocalBestStreak = useCallback((winHistoryKey) => {
    try {
      const bestStreakKey = `${winHistoryKey}_best`;
      const savedBestStreak = localStorage.getItem(bestStreakKey);
      
      if (savedBestStreak) {
        return parseInt(savedBestStreak, 10);
      }
      
      return 0;
    } catch (error) {
      console.error('Error getting local best streak:', error);
      return 0;
    }
  }, []);

  /**
   * Get the current streak for a guest by calculating it from localStorage history.
   * PROJECT PHOENIX: Always calculates on-demand, never stale
   * 
   * @param {string} winHistoryKey - The localStorage key for this game's win history  
   * @returns {number} The calculated current streak
   */
  const getLocalStreak = useCallback((winHistoryKey) => {
    try {
      const savedHistory = localStorage.getItem(winHistoryKey);
      if (!savedHistory) return 0;

      const parsed = JSON.parse(savedHistory);
      
      // Handle both new and old formats for backward compatibility
      if (Array.isArray(parsed)) {
        // New format: array of date strings
        return calculateLocalStreak(parsed);
      } else if (parsed.count && parsed.lastCompletedDate) {
        // Old format: { count, lastCompletedDate } - calculate directly for compatibility
        const today = new Date(getCentralDateString());
        const lastDate = new Date(parsed.lastCompletedDate);
        const daysDifference = Math.round((today - lastDate) / (1000 * 60 * 60 * 24));
        
        if (daysDifference <= 1) {
          return parsed.count;
        } else {
          return 0; // Streak broken
        }
      }
      
      return 0;
    } catch (error) {
      console.error('Error getting local streak:', error);
      return 0;
    }
  }, [calculateLocalStreak]);

  return {
    // Authenticated user functions
    trackGameCompletion,
    getGameStats,
    
    // PROJECT PHOENIX: New guest user functions
    recordLocalDailyWin,
    getLocalStreak,
    getLocalBestStreak,
    calculateLocalStreak,
    
    // Utility
    isAuthenticated
  };
};

export default useGameStats;